import type { Grade } from '@/config/scoring';
import { getSupabaseClient, isSupabaseConfigured } from '@/services/supabaseClient';
import { ensureDiscordAccessToken } from '@/discord';
import { isDiscordActivity } from '@/utils/discordActivity';

export type DailyBest = {
  date: string;
  score: number;
  accuracy: number;
  grade: Grade | '';
  completedAt: string | null;
};

const DAILY_BEST_KEY = 'morse-hero-daily-best';

function loadDailyBestMap(): Record<string, DailyBest> {
  try {
    const raw = localStorage.getItem(DAILY_BEST_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, DailyBest>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function saveDailyBestMap(map: Record<string, DailyBest>): void {
  try {
    localStorage.setItem(DAILY_BEST_KEY, JSON.stringify(map));
  } catch {
    // Ignore write failures.
  }
}

function earliestTimestamp(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a && !b) return null;
  if (!a) return b ?? null;
  if (!b) return a;
  const aTs = Date.parse(a);
  const bTs = Date.parse(b);
  if (Number.isNaN(aTs) && Number.isNaN(bTs)) return a;
  if (Number.isNaN(aTs)) return b;
  if (Number.isNaN(bTs)) return a;
  return aTs <= bTs ? a : b;
}

function mergeBest(local: DailyBest | null, remote: DailyBest | null): DailyBest | null {
  if (!local && !remote) return null;
  if (!local) return remote;
  if (!remote) return local;

  let best = local;
  if (remote.score > local.score) {
    best = {
      ...remote,
      completedAt: earliestTimestamp(local.completedAt, remote.completedAt),
    };
  } else {
    best = {
      ...local,
      completedAt: earliestTimestamp(local.completedAt, remote.completedAt),
    };
  }
  return best;
}

export function getLocalDailyBest(date: string): DailyBest | null {
  const map = loadDailyBestMap();
  return map[date] ?? null;
}

export function updateLocalDailyBest(date: string, result: DailyBest): { best: DailyBest; isNewBest: boolean } {
  const map = loadDailyBestMap();
  const current = map[date] ?? null;

  let isNewBest = false;
  let best = result;

  if (current && current.score >= result.score) {
    best = {
      ...current,
      completedAt: earliestTimestamp(current.completedAt, result.completedAt),
    };
  } else {
    isNewBest = true;
    if (current) {
      best.completedAt = earliestTimestamp(current.completedAt, result.completedAt);
    }
  }

  map[date] = best;
  saveDailyBestMap(map);
  return { best, isNewBest };
}

async function getSessionUserId(): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.auth.getSession();
  if (error) return null;
  return data.session?.user?.id ?? null;
}

function toRemoteKey(date: string): string {
  return `daily:${date}`;
}

type ApiScoreInput = {
  messageKey: string;
  highScore: number;
  highGrade: string;
  highAccuracy: number;
  completedAt: string | null;
};

type ApiSyncResponse = {
  scores: Array<{
    message_key: string;
    high_score: number;
    high_grade: string;
    high_accuracy: number;
    completed_at: string | null;
  }>;
};

async function getSupabaseAccessToken(): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabaseClient();
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function callUserScoresApi(
  action: 'sync' | 'save',
  gameMode: string,
  scores?: ApiScoreInput[],
): Promise<ApiSyncResponse | null> {
  const body: Record<string, unknown> = { action, gameMode };
  if (scores) body.scores = scores;

  // In Discord Activity mode, use Discord token (existing refresh system handles expiry)
  if (isDiscordActivity()) {
    const discordToken = await ensureDiscordAccessToken();
    if (!discordToken) return null;
    body.discordAccessToken = discordToken;
  } else {
    const accessToken = await getSupabaseAccessToken();
    if (!accessToken) return null;
    body.accessToken = accessToken;
  }

  try {
    const response = await fetch('/api/user-scores', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.warn('user-scores API failed:', response.status);
      return null;
    }

    return (await response.json()) as ApiSyncResponse;
  } catch (error) {
    console.warn('user-scores API error:', error);
    return null;
  }
}

async function syncDailyBestViaApi(date: string, localUpdate: { best: DailyBest; isNewBest: boolean }): Promise<{ best: DailyBest; isNewBest: boolean }> {
  const key = toRemoteKey(date);
  const syncResult = await callUserScoresApi('sync', 'daily');

  if (!syncResult) {
    return localUpdate;
  }

  const row = syncResult.scores.find((s) => s.message_key === key);
  const remote = row ? {
    date,
    score: row.high_score,
    accuracy: row.high_accuracy,
    grade: row.high_grade as Grade | '',
    completedAt: row.completed_at,
  } : null;

  const merged = mergeBest(localUpdate.best, remote);
  if (!merged) return localUpdate;

  const remoteNeedsUpdate = !remote
    || remote.score !== merged.score
    || remote.accuracy !== merged.accuracy
    || remote.grade !== merged.grade
    || remote.completedAt !== merged.completedAt;

  if (remoteNeedsUpdate) {
    await callUserScoresApi('save', 'daily', [{
      messageKey: key,
      highScore: merged.score,
      highGrade: merged.grade,
      highAccuracy: merged.accuracy,
      completedAt: merged.completedAt,
    }]);
  }

  const map = loadDailyBestMap();
  map[date] = merged;
  saveDailyBestMap(map);

  return {
    best: merged,
    isNewBest: localUpdate.isNewBest && merged.score === localUpdate.best.score,
  };
}

export async function syncDailyBest(date: string, result: DailyBest): Promise<{ best: DailyBest; isNewBest: boolean }>{
  const localUpdate = updateLocalDailyBest(date, result);

  if (!isSupabaseConfigured()) {
    return localUpdate;
  }

  // Use API in Activity mode to avoid CSP issues
  if (isDiscordActivity()) {
    return syncDailyBestViaApi(date, localUpdate);
  }

  const userId = await getSessionUserId();
  if (!userId) {
    return localUpdate;
  }

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('user_scores')
    .select('high_score,high_grade,high_accuracy,completed_at')
    .eq('game_mode', 'daily')
    .eq('message_key', toRemoteKey(date))
    .limit(1);

  if (error) {
    return localUpdate;
  }

  const row = Array.isArray(data) ? data[0] : null;
  const remote = row ? {
    date,
    score: typeof row.high_score === 'number' ? row.high_score : 0,
    accuracy: typeof row.high_accuracy === 'number' ? row.high_accuracy : 0,
    grade: typeof row.high_grade === 'string' ? row.high_grade : '',
    completedAt: row.completed_at ?? null,
  } as DailyBest : null;

  const merged = mergeBest(localUpdate.best, remote);
  if (!merged) return localUpdate;

  const remoteNeedsUpdate = !remote
    || remote.score !== merged.score
    || remote.accuracy !== merged.accuracy
    || remote.grade !== merged.grade
    || remote.completedAt !== merged.completedAt;

  if (remoteNeedsUpdate) {
    await supabase
      .from('user_scores')
      .upsert({
        user_id: userId,
        game_mode: 'daily',
        message_key: toRemoteKey(date),
        high_score: merged.score,
        high_grade: merged.grade,
        high_accuracy: merged.accuracy,
        completed_at: merged.completedAt,
      }, { onConflict: 'user_id,game_mode,message_key' });
  }

  const map = loadDailyBestMap();
  map[date] = merged;
  saveDailyBestMap(map);

  return {
    best: merged,
    isNewBest: localUpdate.isNewBest && merged.score === localUpdate.best.score,
  };
}
